import { Injectable } from '@angular/core';
import { take } from 'rxjs';
import { CONSTANTS } from '../../Constants/Constants';
import { OpenEstimateResponse, SQWebProjectContainer } from '../../models/dtos';
import { CommonDataService } from '../common-data.service';
import { PreferencesService } from '../preferences/preferences.service';
import { CostReviewFunctionsService } from '../review/cost-review-functions.service';
import { ReviewDetailTabsService } from 'review-detail-tabs';
import { GetReviewDetailsService } from '../api/get-review-details.service';
import { CommonSynchronizationDataService } from './common-synchronization-data.service';
import { EscalationResetService } from '../operations/escalation-reset.service';
import { IndexDBService } from './indexDB.service';
import { SolCalculationsService } from '../operations/sol-calculations.service';
import { JsonDeserializer } from '../utils/json-deserializer';
import { SellAsMinimumMarginService } from '../sell-as-minimum-margin.service';
import { webVersion } from '../../../environments/environment';
import { CostReviewStateSyncService } from '../review/cost-review-state-sync.service';
import { CommonFunctionsService } from '../common-functions.service';

@Injectable({ providedIn: 'root' })
export class OpenEstimateOperationsService {

  constructor(
    private readonly preferencesService: PreferencesService,
    private readonly commonDataService: CommonDataService,
    private readonly costReviewOperationsService: CostReviewFunctionsService,
    private readonly reviewDetailTabsService: ReviewDetailTabsService,
    private readonly reviewDetailsAPIService: GetReviewDetailsService,
    private readonly commonSynchronizationService: CommonSynchronizationDataService,
    private readonly escalationResetService: EscalationResetService,
    private readonly indexDBService: IndexDBService,
    private readonly solCalculationsService: SolCalculationsService,
    private readonly sellAsMinimumMarginService: SellAsMinimumMarginService,
    private readonly costReviewStateSyncService: CostReviewStateSyncService,
    private readonly commonFunctionService: CommonFunctionsService
  ) {}

  async openEstimate(openEstimateResponse: OpenEstimateResponse): Promise<void> {
    const estimateData = openEstimateResponse.estimateData;
    this.openEstimateOperations(openEstimateResponse);
    this.getCostReviewParameterData();
    this.commonSynchronizationService.startSyncPolling();
    await this.openProjectObjectOperations(openEstimateResponse.estimateData.projectObject, estimateData.estimateID);
    await this.sellAsMinimumMarginService.preloadForCurrentEstimate().catch(error => {
      console.error('Minimum margin preload failed', error);
    });
    await this.costReviewStateSyncService.loadCostReviewDetails().catch(error => {
      console.error('Cost review load failed', error);
    });
    this.preferencesService.addToRecentViewedEstimateList(estimateData.estimateID, estimateData.estimateName);
    this.costReviewOperationsService.getUserCostReviewThreshold();
  }

  async openProjectObjectOperations(projectContainer: SQWebProjectContainer, estimateID: string): Promise<void> {
    projectContainer = JsonDeserializer.deserialize(projectContainer);

    projectContainer.metaData.estimateMetaData.estimateSettings.webVersion = webVersion;

    // Publishes the deserialized container (project, metadata, typical-of helpers) as the
    // estimate's project object so every read goes through the same instance.
      this.commonDataService.publishProjectContainer(projectContainer);
      const subsidyValue = projectContainer.metaData.estimateMetaData?.estimateTab?.subsidy ?? CONSTANTS.ZERO;
      if (subsidyValue > CONSTANTS.ZERO) {
          this.commonDataService.notifySubsidyChanged();
      }

    // Check escalation reset before storing data (uses metadata set above)
    const estimateData = this.commonDataService.getEstimateData();
    const isArchived = estimateData?.isArchived ?? false;
    this.escalationResetService.checkEscalationReset(isArchived);

    // Check escalation reset for financial year change
    const collaborationState = this.commonDataService.getCollaborationState();
    this.escalationResetService.resetEscalationIfOpenedInNewFinancialYear(collaborationState);

    // Set project GUID first (required)
    await this.indexDBService.setProjectGuid(estimateID);

    // Run both operations simultaneously
    await Promise.all([
      this.indexDBService.storeAllLevelwiseData(
        projectContainer.materialLevelwiseData,
        projectContainer.laborLevelwiseData,
        projectContainer.miscLevelwiseData,
        projectContainer.drawingLevelwiseData
      ),
      this.solCalculationsService.calculateAllOnEstimateOpen(
        projectContainer.materialLevelwiseData,
        projectContainer.laborLevelwiseData,
        projectContainer.miscLevelwiseData
      )
    ]);

    projectContainer.materialLevelwiseData.materialsCollection = {};
    projectContainer.laborLevelwiseData.laborsCollection = {};
    projectContainer.miscLevelwiseData.miscsCollection = {};
    projectContainer.drawingLevelwiseData.drawingDataCollection = {};
  }

  private openEstimateOperations(response: OpenEstimateResponse): void {
    this.commonDataService.publishOpenEstimate(response);
    // The booking/zone offices and labor rates were re-pointed on the server because the linked
    // opportunity's sales office changed, so enable Save to let the user persist the update.
    if (response.isToEnableSave) {
      this.commonDataService.isSaveEnabled = true;
    }
    this.reviewDetailTabsService.setBusiness(response.estimateData.business);
    this.reviewDetailTabsService.setSubsegmentId(response.estimateData.subSegmentID);
  }

  private getCostReviewParameterData(): void {
    const cultureId = this.commonFunctionService.getCultureId(this.commonDataService.getUserLanguagePreference());
    this.reviewDetailsAPIService.getCostReviewParameterData(cultureId)
      .pipe(take(CONSTANTS.ONE))
      .subscribe({
        next: (parameters) => {
          this.commonDataService.setReviewParameterResponse(parameters);
        },
        error: () => {
          console.error('Error fetching cost review parameter data');
          this.commonDataService.setReviewParameterResponse([]);
        }
      });
  }
}

